// utils/richText.js
// ──────────────────────────────────────────────────────────────────────────────
// Pure helpers for the rich-text fields authored with the ReactQuill editor
// (course description / syllabus / prerequisites / highlights, CMS copy, …).
//
// These live here — not in components/Common/RichText.jsx — so any module can
// flatten stored markup without pulling in Quill's stylesheet. Render full
// content with <RichText/>; use these for card snippets, meta descriptions,
// search haystacks and emptiness checks.
//
// Never interpolate a rich-text field straight into JSX: React escapes the
// markup and the user sees literal `<p>` tags.
// ──────────────────────────────────────────────────────────────────────────────

/** Heuristic: does this string contain HTML markup (i.e. came from the editor)? */
export const isHtml = (s) => typeof s === 'string' && /<[a-z][\s\S]*>/i.test(s);

/**
 * Decode HTML entities (`&amp;`, `&#39;`, `&nbsp;`…) to their characters.
 * A <textarea> has a text-only content model, so assigning innerHTML here never
 * creates elements — this is the standard safe decode. Callers strip tags first.
 */
const decodeEntities = (s) => {
  if (!s.includes('&')) return s;
  try {
    const el = document.createElement('textarea');
    el.innerHTML = s;
    return el.value;
  } catch {
    return s;
  }
};

/** Strip tags/entities down to a plain-text string — for snippets & previews. */
export const stripHtml = (s) => {
  if (typeof s !== 'string' || !s) return '';
  // Tags → space so block boundaries don't glue words together ("A</p><p>B").
  const text = decodeEntities(s.replace(/<[^>]*>/g, ' '));
  return text.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
};

/**
 * Does this field hold anything worth rendering? Text counts, and so does
 * embedded media even with no text around it. A blank Quill editor stores
 * '<p><br></p>', which is truthy but has nothing to show — this returns false
 * for it, so callers don't render empty headings, cards or tabs.
 */
export const hasRichContent = (v) =>
  typeof v === 'string' && (stripHtml(v).length > 0 || /<(img|table|iframe|video|audio|hr)\b/i.test(v));

/**
 * Plain-text preview of a rich-text field, truncated with an ellipsis.
 * Truncating the raw HTML instead would slice tags in half.
 */
export const richTextSnippet = (value, maxLength = 100) => {
  const text = stripHtml(value);
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;
};
